import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import { 
  PhoneIcon,
  SpeakerWaveIcon,
  CloudIcon,
  CpuChipIcon,
  ServerIcon,
  PlayIcon,
  CodeBracketIcon
} from '@heroicons/react/24/outline';

const Home = () => {
  const products = [
    {
      name: "Taar.AI Converse",
      description: "Build sophisticated voice call flows with visual drag-and-drop interface",
      featured: true,
      icon: PlayIcon,
      color: "red",
      cards: [
        {
          icon: SpeakerWaveIcon,
          title: "Converse Flows",
          description: "Visual flow builder for voice conversations",
          link: "/docs/converse-flows"
        }
      ]
    },
    {
      name: "Taar Dialer",
      description: "Advanced voice communication infrastructure for web applications",
      featured: false,
      icon: PhoneIcon,
      color: "blue",
      cards: [
        {
          icon: CodeBracketIcon,
          title: "Agent Registration API",
          description: "Register AI agents and manage authentication",
          link: "/docs/agent-registration-api"
        },
        {
          icon: CpuChipIcon,
          title: "WebRTC Client",
          description: "Integrate voice calling into your applications",
          link: "/docs/webrtc-client"
        },
        {
          icon: ServerIcon,
          title: "Agent Registration Process",
          description: "Complete setup guide for AI agents",
          link: "/docs/agent-registration"
        },
        {
          icon: CloudIcon,
          title: "Autodialer",
          description: "Automated calling with simultaneous dialing",
          link: "/docs/autodialer"
        }
      ]
    }
  ];


  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Taar.AI Documentation
          </h1>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto mb-8">
            India-first, developer-first documentation for a telecom backbone for AI in India.
            Build sophisticated voice applications with our comprehensive platform.
          </p>
          
          {/* Platform Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
            <div className="bg-gradient-to-r from-red-50 to-red-100 p-6 rounded-lg border border-red-200">
              <PlayIcon className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900 mb-1">Visual Voice Flows</h3>
              <p className="text-sm text-gray-600">Drag-drop visual builder for call automation</p>
            </div>
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-lg border border-blue-200">
              <PhoneIcon className="w-8 h-8 text-blue-500 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900 mb-1">WebRTC Infrastructure</h3>
              <p className="text-sm text-gray-600">Enterprise-grade voice communication platform</p>
            </div>
            <div className="bg-gradient-to-r from-green-50 to-green-100 p-6 rounded-lg border border-green-200">
              <CpuChipIcon className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900 mb-1">Developer-First</h3>
              <p className="text-sm text-gray-600">APIs designed by developers, for developers</p>
            </div>
          </div>
        </div>

        {/* Products Section */}
        <div className="space-y-16">
          {products.map((product, productIndex) => (
            <div key={productIndex}>
              {/* Product Header */}
              <div className="flex items-center mb-8">
                <div className={`flex items-center justify-center w-12 h-12 rounded-lg bg-gradient-to-r from-${product.color}-100 to-${product.color}-200 mr-4`}>
                  <product.icon className={`w-6 h-6 text-${product.color}-600`} />
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">{product.name}</h2>
                  <p className="text-gray-600 mt-2">{product.description}</p>
                </div>
                {product.featured && (
                  <div className="ml-auto">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                      <span className="w-2 h-2 bg-red-400 rounded-full mr-2"></span>
                      New & Featured
                    </span>
                  </div>
                )}
              </div>

              {/* Product Cards */}
              <div className={`grid grid-cols-1 ${product.cards.length > 2 ? 'md:grid-cols-2 lg:grid-cols-2' : 'md:grid-cols-2'} gap-6`}>
                {product.cards.map((card, cardIndex) => (
                  <Link
                    key={cardIndex}
                    to={card.link}
                    className={`group block p-6 bg-white rounded-lg border transition-all duration-200 hover:shadow-lg ${
                      product.color === 'red' 
                        ? 'border-red-200 hover:border-red-300' 
                        : 'border-blue-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <card.icon className={`w-8 h-8 ${
                          product.color === 'red' ? 'text-red-500' : 'text-blue-500'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <h3 className={`text-lg font-semibold text-gray-900 group-hover:transition-colors ${
                          product.color === 'red' ? 'group-hover:text-red-600' : 'group-hover:text-blue-600'
                        }`}>
                          {card.title}
                        </h3>
                        <p className="mt-2 text-gray-600">
                          {card.description}
                        </p>
                        <div className="mt-3 flex items-center text-sm font-medium">
                          <span className={`${
                            product.color === 'red' ? 'text-red-600' : 'text-blue-600'
                          } group-hover:text-gray-900 transition-colors`}>
                            Learn more
                          </span>
                          <svg 
                            className={`ml-1 w-4 h-4 transform transition-transform group-hover:translate-x-1`} 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-20 text-center bg-gradient-to-r from-red-50 to-blue-50 p-12 rounded-xl">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Ready to Build Voice Applications?
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Join thousands of developers building the future of voice-enabled AI applications with Taar.AI
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://app.taar.ai/converse" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-red-500 text-white px-8 py-3 rounded-lg hover:bg-red-600 transition-colors font-medium"
            >
              Start with Converse Flows
            </a>
            <a 
              href="https://app.taar.ai" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white text-gray-700 px-8 py-3 rounded-lg hover:bg-gray-50 transition-colors font-medium border border-gray-300"
            >
              Explore Dashboard
            </a>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Home;